import React, { useState, useEffect, createContext, useContext } from 'react';
import { signInWithPopup, GoogleAuthProvider, signOut, onIdTokenChanged } from "firebase/auth";
import { auth } from '../services/firebase';
import { LinksIDService } from '../services/api';

// --- FIREBASE CONFIG ---
const FIREBASE_ENABLED = true; // Enabled for email/password + Google auth

export const AuthContext = createContext();

// Generate unique guest ID
const getOrCreateGuestId = () => {
  let guestId = localStorage.getItem('guestId');
  if (!guestId) {
    guestId = `guest_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem('guestId', guestId);
  }
  return guestId;
};

// Guest user with unique ID
const createGuestUser = () => ({
  uid: getOrCreateGuestId(),
  displayName: 'Guest',
  email: null,
  isGuest: true,
  passport: null,
});

/**
 * Resolve LinksID passport for an authenticated Firebase user.
 * Auto-creates passport on first call.
 * @returns {{ passport: object|null, error: string|null }}
 */
const resolvePassport = async (firebaseUser) => {
  try {
    const token = await firebaseUser.getIdToken();
    const passport = await LinksIDService.getPassport(token);
    console.log(`[LinksID] Passport resolved: @${passport.handle}`);
    return { passport, error: null };
  } catch (err) {
    console.warn('[LinksID] Passport resolution failed:', err.message);
    return { passport: null, error: err.message };
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passportLoading, setPassportLoading] = useState(false);
  const [passportError, setPassportError] = useState(null);

  useEffect(() => {
    if (!auth) {
      setUser(createGuestUser());
      setLoading(false);
      return;
    }

    // Use onIdTokenChanged instead of onAuthStateChanged —
    // fires on sign-in, sign-out, AND token refresh (hourly)
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Authenticated user — resolve LinksID passport
        setUser({
          ...currentUser,
          isGuest: false,
        });
        setPassportLoading(true);
        setPassportError(null);
        const { passport: p, error } = await resolvePassport(currentUser);
        setPassport(p);
        setPassportError(error);
        setPassportLoading(false);
      } else {
        // Signed out — clean up all auth state
        setUser(createGuestUser());
        setPassport(null);
        setPassportError(null);
        setPassportLoading(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      console.error("Sign-in error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('guestId');
      localStorage.removeItem('authToken');
      setUser(createGuestUser());
      setPassport(null);
      setPassportError(null);
    } catch (error) {
      console.error("Sign-out error:", error);
    }
  };

  /**
   * Refresh passport data from LinksID (e.g. after profile update)
   * @returns {{ passport: object|null, error: string|null }}
   */
  const refreshPassport = async () => {
    if (auth?.currentUser) {
      setPassportLoading(true);
      const { passport: p, error } = await resolvePassport(auth.currentUser);
      setPassport(p);
      setPassportError(error);
      setPassportLoading(false);
      return { passport: p, error };
    }
    return { passport: null, error: null };
  };

  return (
    <AuthContext.Provider value={{
      user, passport, loading, passportLoading, passportError,
      login, logout, refreshPassport, auth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { auth, FIREBASE_ENABLED };
